/**
 * Единый статус анкеты: видна ли она другим и что сделать, чтобы стала видна.
 *
 * Раньше об этом говорили четыре экрана разными словами: профиль показывал
 * «на паузе», предпросмотр — «вас пока не показывают», режимы — «вас
 * показывают подходящим людям» (даже при отсутствии проверенного фото),
 * а «Люди» — свой баннер. Здесь одно правило и один текст на всё.
 *
 * Изменение v1.12: всё, что чинит скрытую анкету со стороны подбора, ведёт
 * в P-13 «Подбор» внутри вкладки «Движ» — прежнего `/me/modes` в профиле нет.
 *
 * Порядок проверки — от самого сильного ограничения к самому мягкому:
 * санкция → пауза → нет фото → модерация → режимы.
 *
 * Изменение v1.11: фото публикуется сразу, поэтому состояния «на проверке» больше нет —
 * есть только «фото нет» (в подборе «Движа» без фото не показывают) и «снято модерацией».
 */
import type { Me } from '@shared/api/types'

export type VisibilityState =
  | 'visible'
  | 'hidden_by_sanction'
  | 'paused'
  | 'no_photo'
  | 'moderation_restricted'
  | 'no_mode'
  | 'mode_incomplete'

export interface VisibilityAction {
  label: string
  /** Куда ведёт следующий шаг; `resume` — снять паузу прямо на месте. */
  to: '/me/photos' | '/dvizh/setup' | '/me/edit' | '/settings/restrictions' | 'resume'
}

export interface ProfileVisibility {
  state: VisibilityState
  /** Анкету показывают в рекомендациях. */
  visible: boolean
  /** Короткий заголовок статуса — для карточки в профиле и баннеров. */
  title: string
  /** Что это значит и что произойдёт дальше — одним-двумя предложениями. */
  text: string
  /** Понятный следующий шаг; у видимой анкеты его нет. */
  action: VisibilityAction | null
  tone: 'success' | 'warning' | 'neutral' | 'danger'
  icon: 'check-circle' | 'pause-circle' | 'image' | 'clock' | 'shield' | 'settings'
}

export function profileVisibility(me: Me): ProfileVisibility {
  const sanctions = me.sanctions ?? []
  const hiddenBySanction = sanctions.some((s) => s.type === 'hidden' || s.type === 'banned')
  if (hiddenBySanction) {
    return {
      state: 'hidden_by_sanction',
      visible: false,
      title: 'Анкета скрыта по решению модерации',
      text: 'Новым людям вас не показывают. Диалоги работают как обычно.',
      action: { label: 'Подробнее', to: '/settings/restrictions' },
      tone: 'danger',
      icon: 'shield',
    }
  }

  if (me.profile.paused) {
    return {
      state: 'paused',
      visible: false,
      title: 'Анкета на паузе',
      text: 'Вас не показывают в подборке. Чаты работают как обычно.',
      action: { label: 'Вернуться в подбор', to: 'resume' },
      tone: 'neutral',
      icon: 'pause-circle',
    }
  }

  const visiblePhotos = me.profile.photos.filter((p) => p.state !== 'rejected').length

  if (visiblePhotos === 0) {
    return {
      state: 'no_photo',
      visible: false,
      title: 'Нужно фото',
      text: 'В «Движе» показывают анкеты с фото. В чатах фото необязательно.',
      action: { label: 'Добавить фото', to: '/me/photos' },
      tone: 'warning',
      icon: 'image',
    }
  }

  if (me.profile.moderation_state === 'restricted') {
    return {
      state: 'moderation_restricted',
      visible: false,
      title: 'Анкета скрыта',
      text: 'Что-то не прошло проверку. Исправьте — и анкету снова покажут.',
      action: { label: 'Исправить', to: '/me/edit' },
      tone: 'danger',
      icon: 'shield',
    }
  }

  // Изменение v1.3: единственный режим продукта — friends («Движ»).
  const enabledModes = me.modes.filter((m) => m.enabled && m.mode === 'friends')
  if (!enabledModes.length) {
    return {
      state: 'no_mode',
      visible: false,
      title: 'Подбор выключен',
      text: 'Пока он выключен, подборки нет и вас не показывают.',
      action: { label: 'Включить', to: '/dvizh/setup' },
      tone: 'warning',
      icon: 'settings',
    }
  }

  if (enabledModes.every((m) => m.missing.length > 0)) {
    return {
      state: 'mode_incomplete',
      visible: false,
      title: 'Параметры подбора не заполнены',
      text: 'Без обязательных параметров подбор не запускается.',
      action: { label: 'Заполнить', to: '/dvizh/setup' },
      tone: 'warning',
      icon: 'settings',
    }
  }

  return {
    state: 'visible',
    visible: true,
    title: 'Анкету показывают',
    text: 'Вас видят подходящие люди в «Движе».',
    action: null,
    tone: 'success',
    icon: 'check-circle',
  }
}

/** Причины, которые снимает сам переключатель подбора (P-13). */
export function isModeReason(state: VisibilityState): boolean {
  return state === 'no_mode' || state === 'mode_incomplete'
}

/**
 * Подпись переключателя подбора с учётом общего статуса анкеты (P-13).
 *
 * `enabled` — то, что стоит в форме прямо сейчас, а `visibility` посчитан по
 * сохранённому состоянию. Поэтому причины, которые снимает сам переключатель,
 * здесь не называются: только что включённый подбор иначе жаловался бы на то,
 * что он выключен. А вот про фото, паузу и модерацию сказать обязательно —
 * без этого «включено» обещало бы показ, которого не будет.
 */
export function describeModeHint(visibility: ProfileVisibility, enabled: boolean): string {
  if (!enabled) return 'Ни подборки, ни показа анкеты. Чаты и контакты работают как обычно'
  if (visibility.visible || isModeReason(visibility.state)) {
    return 'Вы видите подборку, и вас показывают в чужой'
  }
  return `Включён, но анкета скрыта: ${visibility.title.toLowerCase()}`
}
